import { useTranslation } from 'react-i18next';
import type { CurrencyCode } from 'pi-kiosk-shared';
import { PaymentMethodOptionCard } from './PaymentMethodPicker.js';

export interface CheckoutCurrencyPickerProps {
  readonly currencies: readonly CurrencyCode[];
  readonly selected: CurrencyCode;
  readonly locked: boolean;
  readonly onSelect: (currency: CurrencyCode) => void;
  readonly pending?: boolean;
}

function currencyTitle(code: CurrencyCode, t: (key: string) => string): string {
  if (code === 'EUR') {
    return t('checkout.currency.eur');
  }
  return t('checkout.currency.czk');
}

function currencyDescription(code: CurrencyCode, t: (key: string) => string): string {
  if (code === 'EUR') {
    return t('checkout.currency.eurHint');
  }
  return t('checkout.currency.czkHint');
}

/**
 * MULTI currency picker — shown before pay when SP accepts ≥2 currencies.
 * Locks after paymentId / QR issued (caller sets `locked`).
 */
export function CheckoutCurrencyPicker({
  currencies,
  selected,
  locked,
  onSelect,
  pending = false,
}: CheckoutCurrencyPickerProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  if (currencies.length < 2) {
    return null;
  }

  return (
    <fieldset
      className="flex flex-col gap-2"
      data-testid="customer-checkout-currency-picker"
      disabled={locked || pending}
    >
      <legend className="mb-1 text-sm font-semibold text-[var(--color-on-surface)]">
        {t('checkout.currency.title')}
      </legend>
      {locked ? (
        <p className="text-xs text-[var(--color-on-surface-muted)]" role="status">
          {t('checkout.currency.lockedHint')}
        </p>
      ) : null}
      <div className="flex flex-col gap-2" role="radiogroup" aria-label={t('checkout.currency.title')}>
        {currencies.map((code) => (
          <PaymentMethodOptionCard
            key={code}
            selected={selected === code}
            disabled={locked || pending}
            onSelect={() => {
              if (!locked && !pending && code !== selected) {
                onSelect(code);
              }
            }}
            title={currencyTitle(code, t)}
            description={currencyDescription(code, t)}
            icon={
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-muted)] text-sm font-bold text-[var(--color-on-surface)]"
                aria-hidden="true"
              >
                {code}
              </span>
            }
            testId={`customer-checkout-currency-${code.toLowerCase()}`}
            name="customer-checkout-currency"
            value={code}
          />
        ))}
      </div>
    </fieldset>
  );
}
